import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Snackbar,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Photo as PhotoIcon,
  VideoLibrary as VideoIcon,
  PictureAsPdf as PdfIcon,
  Audiotrack as AudioIcon,
  MoreVert as MoreVertIcon,
  VisibilityOff as HiddenIcon,
  Visibility as VisibleIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";

interface BlogPost {
  id: string;
  title: string;
  content: string;
  category?: string;
  created_at: string;
  updated_at: string;
  hidden: boolean;
  photos: Array<{ url: string; caption: string }>;
  videos: Array<{ url: string; caption: string }>;
  pdfs: Array<{ url: string; caption: string }>;
  audios: Array<{ url: string; caption: string; transcript: string }>;
}

const AdminBlogManager: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedPostId, setSelectedPostId] = useState<string | null>(null);

  // Category filtering
  const [selectedCategory, setSelectedCategory] = useState<string>("All");
  const [availableCategories] = useState<string[]>([
    "Project",
    "WhatsApp",
    "News",
    "Secret",
  ]);

  const fetchBlogPosts = useCallback(
    async (category?: string) => {
      try {
        setLoading(true);
        setError(null);

        // Build URL with category filter
        let url = "/api/blog/";
        if (category && category !== "All") {
          url += `?category=${encodeURIComponent(category)}`;
        }

        const response = await fetch(url, {
          headers: {
            ...(user?.token ? { Authorization: `Bearer ${user.token}` } : {}),
          },
        });
        if (!response.ok) throw new Error("Failed to fetch blog posts");
        const data = await response.json();
        setBlogPosts(data);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to load blog posts"
        );
      } finally {
        setLoading(false);
      }
    },
    [user?.token]
  );

  // Category filter handler
  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    fetchBlogPosts(category);
  };

  useEffect(() => {
    if (user && user.role !== "admin" && user.role !== "blog") {
      navigate("/");
      return;
    }
    fetchBlogPosts(selectedCategory);
  }, [user, navigate, fetchBlogPosts, selectedCategory]);

  const handleView = (post: BlogPost) => {
    setSelectedPost(post);
    setViewDialogOpen(true);
  };

  const handleEdit = (post: BlogPost) => {
    navigate(`/blog/${post.id}/edit`);
  };

  const handleDelete = (post: BlogPost) => {
    setSelectedPost(post);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedPost) return;
    try {
      setProcessing(true);
      const response = await fetch(`/api/blog/${selectedPost.id}`, {
        method: "DELETE",
        headers: {
          ...(user?.token ? { Authorization: `Bearer ${user.token}` } : {}),
        },
      });
      if (!response.ok) throw new Error("Failed to delete blog post");
      setSuccess("Blog post deleted successfully");
      setDeleteDialogOpen(false);
      setSelectedPost(null);
      fetchBlogPosts();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to delete blog post"
      );
    } finally {
      setProcessing(false);
    }
  };

  const handleToggleVisibility = async (
    postId: string,
    currentHidden: boolean
  ) => {
    try {
      setProcessing(true);
      const endpoint = currentHidden
        ? `/api/blog/${postId}/unhide`
        : `/api/blog/${postId}/hide`;
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          ...(user?.token ? { Authorization: `Bearer ${user.token}` } : {}),
        },
      });
      if (!response.ok)
        throw new Error("Failed to update blog post visibility");
      setSuccess(
        `Blog post ${currentHidden ? "unhidden" : "hidden"} successfully`
      );
      fetchBlogPosts();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to update visibility"
      );
    } finally {
      setProcessing(false);
    }
  };

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    postId: string
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedPostId(postId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedPostId(null);
  };

  const getMediaCount = (post: BlogPost) => {
    return (
      post.photos.length +
      post.videos.length +
      post.pdfs.length +
      post.audios.length
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const truncateText = (text: string, maxLength: number = 50) => {
    return text.length > maxLength
      ? text.substring(0, maxLength) + "..."
      : text;
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1">
          Blog Management
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Filter by Category</InputLabel>
            <Select
              value={selectedCategory}
              onChange={(e) => handleCategoryChange(e.target.value)}
              label="Filter by Category"
              size="small"
            >
              <MenuItem value="All">All Categories</MenuItem>
              {availableCategories.map((category) => (
                <MenuItem key={category} value={category}>
                  {category}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={() => fetchBlogPosts(selectedCategory)}
            sx={{ mr: 2 }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => navigate("/blog/create")}
          >
            Create New Post
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Content Preview</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Media</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Updated</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {blogPosts.map((post) => (
              <TableRow key={post.id} hover>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {post.title}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {truncateText(post.content, 80)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={post.category || "News"}
                    size="small"
                    color={
                      post.category === "Project"
                        ? "primary"
                        : post.category === "WhatsApp"
                        ? "secondary"
                        : post.category === "Secret"
                        ? "error"
                        : "default"
                    }
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                    {post.photos.length > 0 && (
                      <Tooltip title={`${post.photos.length} photos`}>
                        <Chip
                          icon={<PhotoIcon />}
                          label={post.photos.length}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </Tooltip>
                    )}
                    {post.videos.length > 0 && (
                      <Tooltip title={`${post.videos.length} videos`}>
                        <Chip
                          icon={<VideoIcon />}
                          label={post.videos.length}
                          size="small"
                          color="secondary"
                          variant="outlined"
                        />
                      </Tooltip>
                    )}
                    {post.pdfs.length > 0 && (
                      <Tooltip title={`${post.pdfs.length} PDFs`}>
                        <Chip
                          icon={<PdfIcon />}
                          label={post.pdfs.length}
                          size="small"
                          color="error"
                          variant="outlined"
                        />
                      </Tooltip>
                    )}
                    {post.audios.length > 0 && (
                      <Tooltip title={`${post.audios.length} audios`}>
                        <Chip
                          icon={<AudioIcon />}
                          label={post.audios.length}
                          size="small"
                          color="warning"
                          variant="outlined"
                        />
                      </Tooltip>
                    )}
                    {getMediaCount(post) === 0 && (
                      <Chip
                        label="No media"
                        size="small"
                        color="default"
                        variant="outlined"
                      />
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={post.hidden ? "Hidden" : "Visible"}
                    color={post.hidden ? "error" : "success"}
                    size="small"
                    icon={post.hidden ? <HiddenIcon /> : <VisibleIcon />}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="caption">
                    {formatDate(post.created_at)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption">
                    {formatDate(post.updated_at)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    onClick={(e) => handleMenuOpen(e, post.id)}
                    size="small"
                  >
                    <MoreVertIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            const post = blogPosts.find((p) => p.id === selectedPostId);
            if (post) handleView(post);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <ViewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            const post = blogPosts.find((p) => p.id === selectedPostId);
            if (post) handleEdit(post);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            const post = blogPosts.find((p) => p.id === selectedPostId);
            if (post) {
              handleToggleVisibility(post.id, post.hidden);
            }
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            {blogPosts.find((p) => p.id === selectedPostId)?.hidden ? (
              <VisibleIcon fontSize="small" />
            ) : (
              <HiddenIcon fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText>
            {blogPosts.find((p) => p.id === selectedPostId)?.hidden
              ? "Unhide"
              : "Hide"}
          </ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            const post = blogPosts.find((p) => p.id === selectedPostId);
            if (post) handleDelete(post);
            handleMenuClose();
          }}
          sx={{ color: "error.main" }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* View Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Blog Post Details</DialogTitle>
        <DialogContent>
          {selectedPost && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {selectedPost.title}
              </Typography>
              <Typography variant="body1" paragraph>
                {selectedPost.content}
              </Typography>

              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                Media Content ({getMediaCount(selectedPost)} items)
              </Typography>

              <Grid container spacing={2}>
                {selectedPost.photos.map((photo, index) => (
                  <Grid item xs={12} sm={6} md={4} key={`photo-${index}`}>
                    <Card>
                      <CardMedia
                        component="img"
                        height="140"
                        image={photo.url}
                        alt={photo.caption}
                        sx={{ objectFit: "cover" }}
                      />
                      <CardContent>
                        <Typography variant="caption" color="text.secondary">
                          {photo.caption}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}

                {selectedPost.videos.map((video, index) => (
                  <Grid item xs={12} sm={6} md={4} key={`video-${index}`}>
                    <Card>
                      <CardContent>
                        <Typography variant="subtitle2" gutterBottom>
                          {video.caption}
                        </Typography>
                        <video
                          src={video.url}
                          controls
                          style={{
                            width: "100%",
                            height: "140px",
                            objectFit: "cover",
                          }}
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                ))}

                {selectedPost.pdfs.map((pdf, index) => (
                  <Grid item xs={12} sm={6} md={4} key={`pdf-${index}`}>
                    <Card>
                      <CardContent>
                        <Typography variant="subtitle2" gutterBottom>
                          {pdf.caption}
                        </Typography>
                        <Button
                          variant="outlined"
                          startIcon={<PdfIcon />}
                          href={pdf.url}
                          target="_blank"
                          fullWidth
                        >
                          View PDF
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}

                {selectedPost.audios.map((audio, index) => (
                  <Grid item xs={12} sm={6} md={4} key={`audio-${index}`}>
                    <Card>
                      <CardContent>
                        <Typography variant="subtitle2" gutterBottom>
                          {audio.caption}
                        </Typography>
                        <audio
                          src={audio.url}
                          controls
                          style={{ width: "100%" }}
                        />
                        {audio.transcript && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mt: 1, display: "block" }}
                          >
                            {truncateText(audio.transcript, 100)}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          <Button
            onClick={() => {
              setViewDialogOpen(false);
              if (selectedPost) {
                navigate(`/blog/${selectedPost.id}`);
              }
            }}
            variant="contained"
          >
            View Full Post
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{selectedPost?.title}"? This action
            cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={processing}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
            disabled={processing}
          >
            {processing ? <CircularProgress size={20} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
      >
        <Alert onClose={() => setSuccess(null)} severity="success">
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminBlogManager;
